import { useState, useEffect } from 'react';

interface Variant {
  name: string;
  key: string;
  stock: number;
  unlimited: boolean;
}

interface InventoryData {
  product_id: string;
  product_name: string;
  category: string;
  subcategory?: string;
  is_made_to_order: boolean;
  stock_quantity: number;
  unlimited_stock: boolean;
  custom_message: string;
  low_stock_threshold: number;
  variants: Variant[];
}

interface InventoryManagerProps {
  productId: string;
  productName: string;
  category: string;
  subcategory?: string;
  onInventoryUpdate?: (inventory: InventoryData) => void;
}

export default function InventoryManager({ 
  productId, 
  productName, 
  category, 
  subcategory,
  onInventoryUpdate 
}: InventoryManagerProps) {
  const [inventory, setInventory] = useState<InventoryData>({
    product_id: productId,
    product_name: productName,
    category,
    subcategory,
    is_made_to_order: false,
    stock_quantity: 0,
    unlimited_stock: false,
    custom_message: '',
    low_stock_threshold: 2,
    variants: []
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showManager, setShowManager] = useState(false);

  useEffect(() => {
    loadInventory();
  }, [productId]);

  const loadInventory = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/inventory-management?action=get&productId=${productId}`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );

      const data = await response.json();
      
      if (data.inventory) {
        setInventory(data.inventory);
        onInventoryUpdate?.(data.inventory);
      } else {
        // Définir les valeurs par défaut selon la catégorie
        const isMadeToOrder = ['Anges de Noël', 'Sapins de Noël', 'Bougies pliées', 'Personnalisations'].includes(subcategory || '');
        const defaultInventory = {
          ...inventory,
          is_made_to_order: isMadeToOrder,
          unlimited_stock: isMadeToOrder,
          custom_message: isMadeToOrder ? 'Fabriqué sur commande – délai 2 à 3 jours' : ''
        };
        setInventory(defaultInventory);
        onInventoryUpdate?.(defaultInventory);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du stock:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveInventory = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/inventory-management?action=update`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(inventory),
        }
      );

      const data = await response.json();
      
      if (data.success) {
        onInventoryUpdate?.(data.inventory);
        setShowManager(false);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const addVariant = () => {
    setInventory(prev => ({
      ...prev,
      variants: [...prev.variants, { name: '', key: '', stock: 0, unlimited: false }]
    }));
  };

  const updateVariant = (index: number, field: keyof Variant, value: any) => {
    setInventory(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => 
        i === index ? { ...variant, [field]: value } : variant
      )
    }));
  };

  const removeVariant = (index: number) => {
    setInventory(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div className="bg-white p-4 rounded-lg border-2 border-[#C49B86]/20">
        <div className="animate-pulse">
          <div className="h-4 bg-[#C49B86]/20 rounded w-1/3 mb-2"></div>
          <div className="h-3 bg-[#C49B86]/10 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border-2 border-[#C49B86]/20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-[#C49B86] text-lg sm:text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
          <i className="ri-store-line mr-2"></i>
          Gestion des stocks
        </h3>
        <button
          onClick={() => setShowManager(!showManager)}
          className="text-[#C49B86] hover:text-[#B97A72] transition-colors"
        >
          <i className={`ri-${showManager ? 'eye-off' : 'settings'}-line text-lg`}></i>
        </button>
      </div>

      {showManager ? (
        <div className="space-y-6">
          {/* Type de produit */}
          <div>
            <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Type de produit
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={inventory.is_made_to_order}
                  onChange={() => setInventory(prev => ({ 
                    ...prev, 
                    is_made_to_order: true, 
                    unlimited_stock: true,
                    custom_message: prev.custom_message || 'Fabriqué sur commande – délai 2 à 3 jours'
                  }))}
                  className="mr-2 text-[#C49B86]"
                />
                <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Fabriqué sur commande (stock illimité)
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!inventory.is_made_to_order}
                  onChange={() => setInventory(prev => ({ 
                    ...prev, 
                    is_made_to_order: false, 
                    unlimited_stock: false 
                  }))}
                  className="mr-2 text-[#C49B86]"
                />
                <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Stock physique limité
                </span>
              </label>
            </div>
          </div>

          {/* Gestion du stock principal */}
          {!inventory.is_made_to_order && (
            <div>
              <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Quantité en stock
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  value={inventory.stock_quantity}
                  onChange={(e) => setInventory(prev => ({ ...prev, stock_quantity: parseInt(e.target.value) || 0 }))}
                  className="flex-1 px-3 py-2 border border-[#C49B86]/30 rounded-lg focus:outline-none focus:border-[#C49B86]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inventory.unlimited_stock}
                    onChange={(e) => setInventory(prev => ({ ...prev, unlimited_stock: e.target.checked }))}
                    className="mr-2 text-[#C49B86]"
                  />
                  <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Illimité
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Seuil de stock faible */}
          {!inventory.is_made_to_order && !inventory.unlimited_stock && (
            <div>
              <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Seuil de stock faible
              </label>
              <input
                type="number"
                min="0"
                value={inventory.low_stock_threshold}
                onChange={(e) => setInventory(prev => ({ ...prev, low_stock_threshold: parseInt(e.target.value) || 2 }))}
                className="w-full px-3 py-2 border border-[#C49B86]/30 rounded-lg focus:outline-none focus:border-[#C49B86]"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              />
            </div>
          )}

          {/* Message personnalisé */}
          <div>
            <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Message affiché au client
            </label>
            <textarea
              value={inventory.custom_message}
              onChange={(e) => setInventory(prev => ({ ...prev, custom_message: e.target.value }))}
              placeholder="Ex: Fabriqué sur commande – délai 2 à 3 jours"
              className="w-full px-3 py-2 border border-[#C49B86]/30 rounded-lg focus:outline-none focus:border-[#C49B86] resize-none"
              rows={2}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>

          {/* Gestion des variantes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Variantes du produit
              </label>
              <button
                onClick={addVariant}
                className="text-[#C49B86] hover:text-[#B97A72] transition-colors text-sm"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-add-line mr-1"></i>
                Ajouter une variante
              </button>
            </div>
            
            {inventory.variants.map((variant, index) => (
              <div key={index} className="bg-[#FAF9F8] p-3 rounded-lg mb-3 border border-[#C49B86]/20">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Nom de la variante"
                    value={variant.name}
                    onChange={(e) => updateVariant(index, 'name', e.target.value)}
                    className="px-3 py-2 border border-[#C49B86]/30 rounded-lg focus:outline-none focus:border-[#C49B86] text-sm"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <input
                    type="text"
                    placeholder="Clé (ex: rouge, blanc)"
                    value={variant.key}
                    onChange={(e) => updateVariant(index, 'key', e.target.value)}
                    className="px-3 py-2 border border-[#C49B86]/30 rounded-lg focus:outline-none focus:border-[#C49B86] text-sm"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="Stock"
                      value={variant.stock}
                      onChange={(e) => updateVariant(index, 'stock', parseInt(e.target.value) || 0)}
                      disabled={variant.unlimited}
                      className="w-20 px-2 py-1 border border-[#C49B86]/30 rounded focus:outline-none focus:border-[#C49B86] text-sm disabled:bg-gray-100"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    />
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={variant.unlimited}
                        onChange={(e) => updateVariant(index, 'unlimited', e.target.checked)}
                        className="mr-1 text-[#C49B86]"
                      />
                      <span className="text-xs text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Illimité
                      </span>
                    </label>
                  </div>
                  
                  <button
                    onClick={() => removeVariant(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Boutons d'action */}
          <div className="flex space-x-3 pt-4 border-t border-[#C49B86]/20">
            <button
              onClick={saveInventory}
              disabled={isSaving}
              className="flex-1 bg-[#C49B86] text-white py-2 px-4 rounded-lg hover:bg-[#B97A72] transition-colors disabled:opacity-50 text-sm font-medium"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {isSaving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Sauvegarder
                </>
              )}
            </button>
            
            <button
              onClick={() => setShowManager(false)}
              className="px-4 py-2 border border-[#C49B86] text-[#C49B86] rounded-lg hover:bg-[#C49B86]/10 transition-colors text-sm font-medium"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Affichage du statut actuel */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Type :
            </span>
            <span className="text-sm font-medium text-[#C49B86]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {inventory.is_made_to_order ? 'Sur commande' : 'Stock physique'}
            </span>
          </div>
          
          {!inventory.is_made_to_order && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Stock :
              </span>
              <span className="text-sm font-medium text-[#C49B86]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {inventory.unlimited_stock ? 'Illimité' : `${inventory.stock_quantity} unités`}
              </span>
            </div>
          )}
          
          {inventory.custom_message && (
            <div className="bg-[#EBD2CF] p-3 rounded-lg">
              <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {inventory.custom_message}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}